'use client';

/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable no-multi-assign */
import cloneDeep from 'lodash-es/cloneDeep';
import { useTransform, type MotionValue } from 'motion/react';

export type MotionGesture = 'tap' | 'hover' | 'inView' | 'scroll' | 'repeat';

export type GestureEvents = 'whileTap' | 'whileHover' | 'whileInView';

export type MotionEffectMoveType =
    | 'move↑'
    | 'move↗'
    | 'move→'
    | 'move↘'
    | 'move↓'
    | 'move↙'
    | 'move←'
    | 'move↖';

export type MotionEffectType =
    | 'scale↑'
    | 'scale↓'
    | 'fadeIn'
    | 'rotate'
    | 'bounce'
    | MotionEffectMoveType;

export type MotionRange = 'S' | 'M' | 'L';

export type MotionEffectBounceValue = number[]; // [y1, y2, y3...]

export type MotionEffectDurationTime = number;

export type MotionDuration = 'S' | 'M' | 'L';

export const MotionRangeValueOfRotate: Record<MotionRange, number> = {
    S: 2,
    M: 4,
    L: 8,
};

export const MotionRangeValueOfBounce: Record<
    MotionRange,
    [MotionEffectBounceValue, MotionEffectDurationTime]
> = {
    S: [[0, -7, -10], 0.4],
    M: [[0, -15, -20], 0.45],
    L: [[0, -25, -30], 0.5],
};

export const motionDurations: Record<MotionDuration, number> = {
    S: 0.15,
    M: 0.5,
    L: 1.4,
};

export const motionVariantsTo = {
    'move↑S': {
        y: -10,
    },
    'move↑M': {
        y: -30,
    },
    'move↑L': {
        y: -50,
    },
    'move↗S': {
        x: 10,
        y: -10,
    },
    'move↗M': {
        x: 30,
        y: -30,
    },
    'move↗L': {
        x: 50,
        y: -50,
    },
    'move→S': {
        x: 10,
    },
    'move→M': {
        x: 30,
    },
    'move→L': {
        x: 50,
    },
    'move↘S': {
        x: 10,
        y: 10,
    },
    'move↘M': {
        x: 30,
        y: 30,
    },
    'move↘L': {
        x: 50,
        y: 50,
    },
    'move↓S': {
        y: 10,
    },
    'move↓M': {
        y: 30,
    },
    'move↓L': {
        y: 50,
    },
    'move↙S': {
        x: -10,
        y: 10,
    },
    'move↙M': {
        x: -30,
        y: 30,
    },
    'move↙L': {
        x: -50,
        y: 50,
    },
    'move←S': {
        x: -10,
    },
    'move←M': {
        x: -30,
    },
    'move←L': {
        x: -50,
    },
    'move↖S': {
        x: -10,
        y: -10,
    },
    'move↖M': {
        x: -30,
        y: -30,
    },
    'move↖L': {
        x: -50,
        y: -50,
    },
    'scale↑S': {
        scale: 1.05,
    },
    'scale↑M': {
        scale: 1.1,
    },
    'scale↑L': {
        scale: 1.15,
    },
    'scale↓S': {
        scale: 0.95,
    },
    'scale↓M': {
        scale: 0.9,
    },
    'scale↓L': {
        scale: 0.85,
    },
    fadeIn: {
        opacity: 1,
    },
};

motionVariantsTo['fadeInS'] =
    motionVariantsTo['fadeInM'] =
    motionVariantsTo['fadeInL'] =
        motionVariantsTo['fadeIn'];

export const motionVariantsToBackward = {
    'move↑S': {
        y: 10,
    },
    'move↑M': {
        y: 30,
    },
    'move↑L': {
        y: 50,
    },
    'move↗S': {
        x: -10,
        y: 10,
    },
    'move↗M': {
        x: -30,
        y: 30,
    },
    'move↗L': {
        x: -50,
        y: 50,
    },
    'move→S': {
        x: -10,
    },
    'move→M': {
        x: -30,
    },
    'move→L': {
        x: -50,
    },
    'move↘S': {
        x: -10,
        y: -10,
    },
    'move↘M': {
        x: -30,
        y: -30,
    },
    'move↘L': {
        x: -50,
        y: -50,
    },
    'move↓S': {
        y: -10,
    },
    'move↓M': {
        y: -30,
    },
    'move↓L': {
        y: -50,
    },
    'move↙S': {
        x: 10,
        y: -10,
    },
    'move↙M': {
        x: 30,
        y: -30,
    },
    'move↙L': {
        x: 50,
        y: -50,
    },
    'move←S': {
        x: 10,
    },
    'move←M': {
        x: 30,
    },
    'move←L': {
        x: 50,
    },
    'move↖S': {
        x: 10,
        y: 10,
    },
    'move↖M': {
        x: 30,
        y: 30,
    },
    'move↖L': {
        x: 50,
        y: 50,
    },
    'scale↑S': {
        scale: 0.95,
    },
    'scale↑M': {
        scale: 0.9,
    },
    'scale↑L': {
        scale: 0.85,
    },
    'scale↓S': {
        scale: 1.05,
    },
    'scale↓M': {
        scale: 1.1,
    },
    'scale↓L': {
        scale: 1.15,
    },
    fadeIn: {
        opacity: 0,
    },
};

motionVariantsToBackward['fadeInS'] =
    motionVariantsToBackward['fadeInM'] =
    motionVariantsToBackward['fadeInL'] =
        motionVariantsToBackward['fadeIn'];

export const motionVariantsOrigin = {
    'move↑S': {
        y: 0,
    },
    'move↑M': {
        y: 0,
    },
    'move↑L': {
        y: 0,
    },
    'move↗S': {
        x: 0,
        y: 0,
    },
    'move↗M': {
        x: 0,
        y: 0,
    },
    'move↗L': {
        x: 0,
        y: 0,
    },
    'move→S': {
        x: 0,
    },
    'move→M': {
        x: 0,
    },
    'move→L': {
        x: 0,
    },
    'move↘S': {
        x: 0,
        y: 0,
    },
    'move↘M': {
        x: 0,
        y: 0,
    },
    'move↘L': {
        x: 0,
        y: 0,
    },
    'move↓S': {
        y: 0,
    },
    'move↓M': {
        y: 0,
    },
    'move↓L': {
        y: 0,
    },
    'move↙S': {
        x: 0,
        y: 0,
    },
    'move↙M': {
        x: 0,
        y: 0,
    },
    'move↙L': {
        x: 0,
        y: 0,
    },
    'move←S': {
        x: 0,
    },
    'move←M': {
        x: 0,
    },
    'move←L': {
        x: 0,
    },
    'move↖S': {
        x: 0,
        y: 0,
    },
    'move↖M': {
        x: 0,
        y: 0,
    },
    'move↖L': {
        x: 0,
        y: 0,
    },
    'scale↑S': {
        scale: 1,
    },
    'scale↑M': {
        scale: 1,
    },
    'scale↑L': {
        scale: 1,
    },
    'scale↓S': {
        scale: 1,
    },
    'scale↓M': {
        scale: 1,
    },
    'scale↓L': {
        scale: 1,
    },
    fadeIn: {
        opacity: 1,
    },
};

motionVariantsOrigin['fadeInS'] =
    motionVariantsOrigin['fadeInM'] =
    motionVariantsOrigin['fadeInL'] =
        motionVariantsOrigin['fadeIn'];

export const motionScrollVariants = {
    'move↑S': {
        y: [100, -100],
    },
    'move↑M': {
        y: [200, -200],
    },
    'move↑L': {
        ...(typeof window !== 'undefined' && {
            y: [window.innerHeight / 2, -window.innerHeight / 2],
        }),
    },
    'move↗S': {
        x: [100, -100],
        y: [100, -100],
    },
    'move↗M': {
        x: [200, -200],
        y: [200, -200],
    },
    'move↗L': {
        ...(typeof window !== 'undefined' && {
            x: [window.innerWidth / 2, -window.innerWidth / 2],
            y: [window.innerHeight / 2, -window.innerHeight / 2],
        }),
    },
    'move→S': {
        x: [100, -100],
    },
    'move→M': {
        x: [200, -200],
    },
    'move→L': {
        ...(typeof window !== 'undefined' && {
            x: [window.innerWidth / 2, -window.innerWidth / 2],
        }),
    },
    'move↘S': {
        x: [100, -100],
        y: [100, -100],
    },
    'move↘M': {
        x: [200, -200],
        y: [200, -200],
    },
    'move↘L': {
        ...(typeof window !== 'undefined' && {
            x: [window.innerWidth / 2, -window.innerWidth / 2],
            y: [window.innerHeight / 2, -window.innerHeight / 2],
        }),
    },
    'move↓S': {
        y: [100, -100],
    },
    'move↓M': {
        y: [200, -200],
    },
    'move↓L': {
        ...(typeof window !== 'undefined' && {
            y: [window.innerHeight / 2, -window.innerHeight / 2],
        }),
    },
    'move↙S': {
        x: [100, -100],
        y: [100, -100],
    },
    'move↙M': {
        x: [200, -200],
        y: [200, -200],
    },
    'move↙L': {
        ...(typeof window !== 'undefined' && {
            x: [window.innerWidth / 2, -window.innerWidth / 2],
            y: [window.innerHeight / 2, -window.innerHeight / 2],
        }),
    },
    'move←S': {
        x: [100, -100],
    },
    'move←M': {
        x: [200, -200],
    },
    'move←L': {
        ...(typeof window !== 'undefined' && {
            x: [window.innerWidth / 2, -window.innerWidth / 2],
        }),
    },
    'move↖S': {
        x: [100, -100],
        y: [100, -100],
    },
    'move↖M': {
        x: [200, -200],
        y: [200, -200],
    },
    'move↖L': {
        ...(typeof window !== 'undefined' && {
            x: [window.innerWidth / 2, -window.innerWidth / 2],
            y: [window.innerHeight / 2, -window.innerHeight / 2],
        }),
    },
    'scale↑S': {
        scale: [0.95, 1],
    },
    'scale↑M': {
        scale: [0.9, 1],
    },
    'scale↑L': {
        scale: [0.85, 1],
    },
    'scale↓S': {
        scale: [1, 0.95],
    },
    'scale↓M': {
        scale: [1, 0.9],
    },
    'scale↓L': {
        scale: [1, 0.85],
    },
    fadeIn: {
        opacity: [0, 1],
    },
    rotateS: {
        rotate: [-5, 5],
    },
    rotateM: {
        rotate: [-15, 15],
    },
    rotateL: {
        rotate: [-35, 35],
    },
};

motionScrollVariants['fadeInS'] =
    motionScrollVariants['fadeInM'] =
    motionScrollVariants['fadeInL'] =
        motionScrollVariants['fadeIn'];

export function getGesture(motionGesture: MotionGesture) {
    const getGestureObject = (gesture: typeof motionGesture) => {
        const key = `while${gesture.charAt(0).toUpperCase() + gesture.slice(1)}`;

        return {
            [key]: motionGesture,
        };
    };

    switch (motionGesture) {
        case 'tap':
            return getGestureObject('tap');
        case 'hover':
            return getGestureObject('hover');
        case 'inView':
            return getGestureObject('inView');
        default:
            return {};
    }
}

export function getAppliedVariants(
    motionEffects: [MotionEffectType, MotionRange, MotionDuration][]
) {
    return motionEffects.map(
        ([motionEffect, motionRange]) => `${motionEffect}${motionRange}`
    );
}

export function getInitial(motionGesture: MotionGesture, appliedVariants: string[]) {
    const initial = {} as Record<MotionEffectType, unknown>;

    for (const variant of appliedVariants) {
        switch (motionGesture) {
            case 'tap':
            case 'hover':
                Object.assign(initial, cloneDeep(motionVariantsOrigin[variant]));
                break;

            case 'inView':
                Object.assign(initial, cloneDeep(motionVariantsToBackward[variant]));
                break;
        }
    }

    return initial;
}

export function getVariantsDuration(
    motionEffects: [MotionEffectType, MotionRange, MotionDuration][]
) {
    const variants = {} as Record<MotionEffectType, MotionDuration>;

    for (const [motionEffect, range, motionDuration] of motionEffects) {
        variants[`${motionEffect}${range}`] = motionDuration;
    }

    return variants;
}

export function getVariants(
    motionGesture: MotionGesture,
    appliedVariants: string[],
    variantsDuration: ReturnType<typeof getVariantsDuration>,
    motionStaggeringChildren: boolean
) {
    const variants = {} as Record<MotionEffectType, unknown>;

    for (const variant of appliedVariants) {
        const transition = {
            duration: motionDurations[variantsDuration[variant]],
        };

        if (motionStaggeringChildren) {
            transition['staggerChildren'] = 0.2;
            transition['delayChildren'] = 0.35;
        }

        switch (motionGesture) {
            case 'tap':
            case 'hover':
                variants[motionGesture] = Object.assign(
                    variants[motionGesture] || {},
                    cloneDeep(motionVariantsTo[variant]),
                    { transition }
                );
                break;
            case 'inView':
                variants[motionGesture] = Object.assign(
                    variants[motionGesture] || {},
                    cloneDeep(motionVariantsOrigin[variant]),
                    { transition }
                );
                break;
        }
    }

    return variants;
}

export function useParallax(value: MotionValue<number>, range: [number, number]) {
    return useTransform(value, [0, 1], range);
}

export function getMotionStyle(
    motionEffects: [MotionEffectType, MotionRange, MotionDuration][],
    scrollYProgress: MotionValue<number>
) {
    const motionStyle = {} as Record<MotionEffectType, unknown>;

    for (const [motionEffect, motionRange] of motionEffects) {
        const transformStyles =
            motionScrollVariants[`${motionEffect}${motionRange || ''}`];

        for (const [style, transformRange] of Object.entries(transformStyles)) {
            motionStyle[style] = useParallax(
                scrollYProgress,
                transformRange as [number, number]
            );
        }
    }

    return motionStyle;
}

export function getMotionRepeatAnimation(
    motionEffects: [MotionEffectType, MotionRange, MotionDuration][]
) {
    const animate = {} as Record<MotionEffectType, unknown>;

    const transition = {} as Record<MotionEffectType, unknown>;

    for (const [motionEffect, motionRange] of motionEffects) {
        switch (motionEffect) {
            case 'rotate': {
                animate.rotate = 360;

                transition.rotate = {
                    repeat: Infinity,
                    repeatType: 'loop',
                    duration: MotionRangeValueOfRotate[motionRange],
                    ease: 'linear',
                };

                break;
            }

            case 'bounce': {
                const [bounceValues, bounceDuration] =
                    MotionRangeValueOfBounce[motionRange];

                // @ts-ignore
                animate.y = bounceValues;

                // @ts-ignore
                transition.y = {
                    repeat: Infinity,
                    repeatType: 'reverse',
                    repeatDelay: 0.1,
                    duration: bounceDuration,
                    ease: 'easeOut',
                };

                break;
            }
        }
    }

    return { animate, transition };
}
